// Core dependencies for date range picker functionality
import styled from "styled-components";
import { memo, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { CheckProductAvailsDateRange } from "@interfaces";
import { theme } from "@utilities";

export interface IAvailabilityRangePickerProps {
    range: CheckProductAvailsDateRange;
    limitDate: {
        from: Date;
        to: Date;
    };
    onRangeChange: (startDate: Date, numDays: number) => void;
    maxDays?: number;
}

export const AvailabilityRangePicker = memo((props: IAvailabilityRangePickerProps) => {
    const { range, limitDate, onRangeChange, maxDays } = props;

    // State for managing date values in YYYY-MM-DD format
    const [startDate, setStartDate] = useState(moment(range.startDate).format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(
        moment(range.startDate)
            .add(range.numDays - 1, "days")
            .format("YYYY-MM-DD"),
    );

    // Calendar UI state management
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(moment(range.startDate));
    const [selectingStart, setSelectingStart] = useState(true); // Track which date user is selecting
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0, width: 0 });
    const [tooltip, setTooltip] = useState<{ text: string; visible: boolean }>({
        text: "",
        visible: false,
    });
    const containerRef = useRef<HTMLDivElement>(null);

    // Handle start date changes from hidden native input
    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newStartDate = e.target.value;
        setStartDate(newStartDate);
        setCurrentMonth(moment(newStartDate));

        const start = moment(newStartDate);
        const end = moment(endDate);

        // Validate dates and trigger callback if valid range
        if (start.isValid() && end.isValid() && start.isSameOrBefore(end)) {
            onRangeChange(start.toDate(), end.diff(start, "days") + 1);
        }
    };

    // Handle end date changes from hidden native input
    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newEndDate = e.target.value;
        setEndDate(newEndDate);

        const start = moment(startDate);
        const end = moment(newEndDate);

        // Validate dates and trigger callback if valid range
        if (start.isValid() && end.isValid() && start.isSameOrBefore(end)) {
            onRangeChange(start.toDate(), end.diff(start, "days") + 1);
        }
    };

    // Sync internal state when external range prop changes
    useEffect(() => {
        setStartDate(moment(range.startDate).format("YYYY-MM-DD"));
        setEndDate(
            moment(range.startDate)
                .add(range.numDays - 1, "days")
                .format("YYYY-MM-DD"),
        );
    }, [range]);

    // Format date for display in DD/MM/YYYY format
    const formatDisplayDate = (date: string) => {
        return moment(date).format("DD/MM/YYYY");
    };

    // Toggle calendar popup and calculate positioning
    const handleCalendarClick = () => {
        if (!isCalendarOpen && containerRef.current) {
            // Calculate popup position relative to input field with 5px gap
            const rect = containerRef.current.getBoundingClientRect();
            setPopupPosition({
                top: rect.bottom + window.scrollY + 5,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
            // Set calendar to show the month of the selected start date
            setCurrentMonth(moment(range.startDate));
        }
        setIsCalendarOpen(!isCalendarOpen);
    };

    // Handle date selection in calendar - supports two-step range selection
    const handleDateClick = (date: moment.Moment, isDisabled: boolean, tooltipText: string) => {
        if (isDisabled) {
            setTooltip({ text: tooltipText, visible: true });
            setTimeout(() => setTooltip({ text: "", visible: false }), 2000);
            return;
        }

        if (selectingStart) {
            // First click: set start date
            setStartDate(date.format("YYYY-MM-DD"));
            setSelectingStart(false);
        } else {
            // Second click: set end date and complete range selection
            const start = moment(startDate);
            const finalStart = date.isBefore(start) ? date : start;
            let finalEnd = date.isBefore(start) ? start : date;

            // Enforce maxDays constraint
            if (maxDays && finalEnd.diff(finalStart, "days") + 1 > maxDays) {
                finalEnd = finalStart.clone().add(maxDays - 1, "days");
            }

            setStartDate(finalStart.format("YYYY-MM-DD"));
            setEndDate(finalEnd.format("YYYY-MM-DD"));
            setSelectingStart(true);
            onRangeChange(finalStart.toDate(), finalEnd.diff(finalStart, "days") + 1);
            setIsCalendarOpen(false);
        }
    };

    // Check if navigating to previous month would go before limit date
    const isPrevMonthDisabled = () => {
        const prevMonth = currentMonth.clone().subtract(1, "month");
        const endOfPrevMonth = prevMonth.clone().endOf("month");
        const limitFrom = moment(limitDate.from);
        // Disable if the entire previous month is before the limit date
        return endOfPrevMonth.isBefore(limitFrom, "day");
    };

    // Check if navigating to next month would go after limit date
    const isNextMonthDisabled = () => {
        const nextMonth = currentMonth.clone().add(1, "month");
        const startOfNextMonth = nextMonth.clone().startOf("month");
        const limitTo = moment(limitDate.to);
        // Disable if the entire next month is after the limit date
        return startOfNextMonth.isAfter(limitTo, "day");
    };

    // Generate calendar grid with proper date highlighting
    const renderCalendar = () => {
        // Calculate the full calendar grid (6 weeks)
        const startOfMonth = currentMonth.clone().startOf("month");
        const endOfMonth = currentMonth.clone().endOf("month");
        const startOfWeek = startOfMonth.clone().startOf("week");
        const endOfWeek = endOfMonth.clone().endOf("week");

        // Build array of all days to display
        const days = [];
        const current = startOfWeek.clone();

        while (current.isSameOrBefore(endOfWeek)) {
            days.push(current.clone());
            current.add(1, "day");
        }

        const startMoment = moment(startDate);
        const endMoment = moment(endDate);
        const limitFrom = moment(limitDate.from);
        const limitTo = moment(limitDate.to);

        // Render each day with appropriate styling
        return days.map((day, index) => {
            const isSelected = day.isSame(startMoment, "day") || day.isSame(endMoment, "day");
            const isInRange = day.isBetween(startMoment, endMoment, "day", "[]");
            const isCurrentMonth = day.isSame(currentMonth, "month");
            const isDisabled = day.isBefore(limitFrom, "day") || day.isAfter(limitTo, "day");

            const tooltipText = isDisabled
                ? day.isAfter(limitTo, "day")
                    ? `Not available after ${limitTo.format("DD/MM/YYYY")}`
                    : `Not available before ${limitFrom.format("DD/MM/YYYY")}`
                : "";

            return (
                <DayCell
                    key={index}
                    isSelected={isSelected}
                    isInRange={isInRange && !isSelected}
                    isDisabled={isDisabled}
                    onClick={() => handleDateClick(day, isDisabled, tooltipText)}
                    onMouseEnter={() =>
                        isDisabled && setTooltip({ text: tooltipText, visible: true })
                    }
                    onMouseLeave={() => setTooltip({ text: "", visible: false })}
                    style={{ opacity: isCurrentMonth && !isDisabled ? 1 : 0.3 }}
                >
                    {day.date()}
                </DayCell>
            );
        });
    };

    // Close calendar when clicking outside
    useEffect(() => {
        const handleClickOutside: (event: MouseEvent) => void = (event: MouseEvent) => {
            const target = event.target as Node;
            const isClickInsideContainer = containerRef.current?.contains(target);
            const isClickInsidePopup =
                target && (target as Element).closest("[data-calendar-popup]");

            // Close if click is outside both container and popup
            if (!isClickInsideContainer && !isClickInsidePopup) {
                setIsCalendarOpen(false);
            }
        };

        if (isCalendarOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isCalendarOpen]);

    return (
        <DateRangeContainer ref={containerRef}>
            <DateRangeInput onClick={handleCalendarClick} data-gtm-id="check-dates">
                <DateText>
                    {formatDisplayDate(startDate)} – {formatDisplayDate(endDate)}
                </DateText>
                <CalendarIcon />
            </DateRangeInput>

            {/* Calendar popup rendered as portal for proper z-index layering */}
            {isCalendarOpen &&
                createPortal(
                    <CalendarPopup
                        isOpen={isCalendarOpen}
                        top={popupPosition.top}
                        left={popupPosition.left}
                        width={popupPosition.width}
                        data-calendar-popup
                    >
                        {/* Calendar header with month navigation */}
                        <CalendarHeader>
                            <NavButton
                                onClick={() =>
                                    setCurrentMonth(currentMonth.clone().subtract(1, "month"))
                                }
                                disabled={isPrevMonthDisabled()}
                            >
                                <svg viewBox="0 0 24 24">
                                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                                </svg>
                            </NavButton>
                            <MonthYear>{currentMonth.format("MMMM YYYY")}</MonthYear>
                            <NavButton
                                onClick={() =>
                                    setCurrentMonth(currentMonth.clone().add(1, "month"))
                                }
                                disabled={isNextMonthDisabled()}
                            >
                                <svg viewBox="0 0 24 24">
                                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                                </svg>
                            </NavButton>
                        </CalendarHeader>

                        {/* Calendar grid with day headers and date cells */}
                        <CalendarGrid>
                            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                                <DayHeader key={day}>{day}</DayHeader>
                            ))}
                            {renderCalendar()}
                        </CalendarGrid>

                        {/* Tooltip for disabled dates */}
                        {tooltip.visible && <Tooltip>{tooltip.text}</Tooltip>}
                    </CalendarPopup>,
                    document.body,
                )}

            {/* Hidden native date inputs for accessibility and form integration */}
            <HiddenInputs>
                <DateInput
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    min={moment(limitDate.from).format("YYYY-MM-DD")}
                    max={moment(limitDate.to).format("YYYY-MM-DD")}
                />
                <DateInput
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    min={moment(limitDate.from).format("YYYY-MM-DD")}
                    max={moment(limitDate.to).format("YYYY-MM-DD")}
                />
            </HiddenInputs>
        </DateRangeContainer>
    );
});

// Main container for the date range picker component
const DateRangeContainer = styled.div`
    position: relative;
    display: block;
    width: 100%;
`;

// Styled input field that displays selected date range with black background
const DateRangeInput = styled.div`
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background: #2a2a2a;
    border: 1px solid #444;
    border-radius: 8px;
    color: #fff;
    font-size: 14px;
    cursor: pointer;
    width: 100%;
    box-sizing: border-box;

    &:hover {
        border-color: ${theme.colors.main};
    }
`;

// Text display for the formatted date range
const DateText = styled.span`
    flex: 1;
    text-align: center;
`;

// Calendar icon displayed on the right side of the input
const CalendarIcon = styled.div`
    width: 20px;
    height: 20px;
    margin-left: 12px;
    background-image: url("/images/calender.svg");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    flex-shrink: 0;
`;

// Hidden native date inputs for accessibility and form integration
const HiddenInputs = styled.div`
    position: absolute;
    opacity: 0;
    pointer-events: none;
`;

// Minimal styled native date input (hidden from view)
const DateInput = styled.input`
    width: 1px;
    height: 1px;
`;

// Floating calendar popup with dynamic positioning and black background
const CalendarPopup = styled.div<{ isOpen: boolean; top: number; left: number; width: number }>`
    position: absolute;
    top: ${(props) => props.top}px;
    left: ${(props) => props.left}px;
    width: ${(props) => props.width}px;
    z-index: 10000;
    background: #1a1a1a;
    border: 1px solid #444;
    border-radius: 8px;
    padding: 16px;
    box-sizing: border-box;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    display: ${(props) => (props.isOpen ? "block" : "none")};
`;

// Header section of calendar with navigation controls
const CalendarHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    color: #fff;
`;

// Display for current month and year
const MonthYear = styled.div`
    font-size: 16px;
    font-weight: 500;
`;

// Navigation buttons for previous/next month
const NavButton = styled.button<{ disabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: ${(props) => (props.disabled ? "#555" : "#fff")};
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
    padding: 8px;
    border-radius: 6px;
    width: 36px;
    height: 36px;
    opacity: ${(props) => (props.disabled ? 0.4 : 1)};

    &:hover {
        background: ${(props) => (props.disabled ? "transparent" : "#333")};
    }

    svg {
        width: 20px;
        height: 20px;
        fill: currentColor;
    }
`;

// 7-column grid layout for calendar days
const CalendarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    margin-bottom: 16px;
`;

// Headers for days of the week (Sun, Mon, etc.)
const DayHeader = styled.div`
    text-align: center;
    padding: 8px 4px;
    font-size: 12px;
    color: #888;
    font-weight: 500;
`;

// Individual day cells with selection and range highlighting
const DayCell = styled.div<{ isSelected?: boolean; isInRange?: boolean; isDisabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
    border-radius: 50%;
    font-size: 14px;
    color: ${(props) =>
        props.isDisabled
            ? "#555"
            : props.isSelected
              ? theme.colors.button_text
              : props.isInRange
                ? theme.colors.button_text
                : "#ccc"};
    background: ${(props) =>
        props.isDisabled
            ? "transparent"
            : props.isSelected
              ? theme.colors.main
              : props.isInRange
                ? `${theme.colors.main}40`
                : "transparent"};

    &:hover {
        background: ${(props) =>
            props.isDisabled ? "transparent" : props.isSelected ? theme.colors.main : "#333"};
    }
`;

// Tooltip for disabled dates
const Tooltip = styled.div`
    position: absolute;
    bottom: 5px;
    left: 50%;
    transform: translateX(-50%);
    color: #fff;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 10001;
    pointer-events: none;
    animation: fadeIn 0.2s ease-in;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;
